/**
 * Public-surface listing status policy — THE single source of truth.
 *
 * COMPLIANCE RULE (absolute, non-negotiable):
 * "Coming Soon" listings MUST NEVER appear on any public-facing surface.
 * Coming Soon is an MLS pre-marketing state. Displaying it publicly — in a
 * search grid, a listing detail page, a sitemap, a count, a map pin, JSON-LD,
 * or an aggregate stat — is a licensing violation for Ryan Realty.
 *
 * Coming Soon IS permitted on:
 *   - /admin/** (broker-authenticated console)
 *   - internal cron / prospecting / compliance / sync tooling
 *   - broker-facing reports and CRM surfaces
 *
 * Before this module existed the status list was copy-pasted into ~20 files
 * across the DAL, the action layer, the sitemap, and SQL. That drift is how
 * Coming Soon reached the public site. Every public-facing status predicate
 * now imports from here. Enforced by scripts/check-public-listing-status.mjs
 * (npm run ci:public-listing-status), wired into ci:gates.
 *
 * If you are adding a public query: import PUBLIC_ACTIVE_STATUSES.
 * If you are adding an admin query: import ADMIN_ACTIVE_STATUSES and add the
 * file to the gate's ADMIN_ALLOWLIST with a reason.
 */

import type { ListingStatus } from '@/lib/data/types/listing'
import {
  OFF_MARKET_STATUSES,
  listingIsOffMarket,
} from '@/lib/listing/publish-listing-published-price'

/** The literal MLS StandardStatus string for pre-marketing listings. */
export const COMING_SOON_STATUS = 'Coming Soon' as const

/**
 * On-market statuses a PUBLIC visitor may see. Coming Soon is deliberately
 * absent — do not add it back.
 */
export const PUBLIC_ACTIVE_STATUSES: ListingStatus[] = ['Active', 'Active Under Contract']

/** Under-contract statuses a public visitor may see. */
export const PUBLIC_PENDING_STATUSES: ListingStatus[] = ['Pending']

/** Everything a public visitor may see on an on-market surface. */
export const PUBLIC_ON_MARKET_STATUSES: ListingStatus[] = [
  ...PUBLIC_ACTIVE_STATUSES,
  ...PUBLIC_PENDING_STATUSES,
]

/**
 * listings.xml: on-market only. Active Under Contract stays (Active family).
 * Pending is included. Closed / Expired / Withdrawn / Canceled / Coming Soon
 * never ship. lastmod is the row's ModificationTimestamp.
 */
export const LISTING_SITEMAP_STATUSES: ListingStatus[] = [...PUBLIC_ON_MARKET_STATUSES]

/**
 * WHAT A PUBLIC COUNT CALLS A LISTING (SITE-193, 2026-09-24).
 *
 * PUBLIC_ACTIVE_STATUSES answers "may a visitor SEE it", and it holds Active
 * Under Contract, because that home still shows and still takes backup
 * offers. It does not answer "is it FOR SALE" in a figure. A home with an
 * accepted offer is under contract: the Market Truth active count is Active
 * alone and puts Active Under Contract in pending_count (see
 * lib/market/publish-place-door.ts), the homepage pulse counts it pending, and
 * the place map draws it as a pending mark. When the homes under that map
 * counted "for sale" off the visibility list instead, /cities/bend printed
 * "700 for sale" on the map and "705 for sale" under it: the same 700 Active
 * listings plus 5 under contract (read 2026-09-24, listing_boundary_xref_mv
 * city/bend against getAtlasTiles(Bend) inside the recorded boundary; the 700
 * listing keys were identical). Every public count that says "for sale" or
 * "pending" reads this one classifier.
 */
export const PUBLIC_FOR_SALE_STATUSES: ListingStatus[] = ['Active']

/** Under contract on a public count: an accepted offer, still showing or not. */
export const PUBLIC_UNDER_CONTRACT_STATUSES: ListingStatus[] = ['Active Under Contract', 'Pending']

export type PublicCountState = 'for-sale' | 'under-contract' | 'sold'

/**
 * The bucket a public count puts a listing in, or null when no public count
 * holds it (Coming Soon, off market, an unknown string). Exact MLS strings, as
 * the tiles carry them.
 */
export function publicCountState(status: string | null | undefined): PublicCountState | null {
  const s = String(status ?? '').trim()
  if ((PUBLIC_FOR_SALE_STATUSES as string[]).includes(s)) return 'for-sale'
  if ((PUBLIC_UNDER_CONTRACT_STATUSES as string[]).includes(s)) return 'under-contract'
  if (s === 'Closed') return 'sold'
  return null
}

/**
 * THE WORD A PUBLIC CARD PRINTS for a listing's status, or null for a home for
 * sale (which prints none). One vocabulary for every listing card and dial on
 * the site (2026-09-25): the homepage shelves called Active Under Contract
 * "Pending" while the place cards and the counts over them called it "under
 * contract". The bucket is publicCountState's, so a card never names a status
 * the count beside it does not hold: Active Under Contract is "Under contract",
 * Pending (the other under-contract status) keeps its MLS word, as the status
 * badge on the photograph prints them (publishListingStatusBadge), and Closed
 * is "Sold". Coming Soon and anything off market print nothing.
 */
export function publicCardStatusLabel(status: string | null | undefined): string | null {
  const state = publicCountState(status)
  if (state === 'under-contract') return String(status ?? '').trim() === 'Pending' ? 'Pending' : 'Under contract'
  if (state === 'sold') return 'Sold'
  return null
}

/**
 * OFF MARKET, for a PUBLIC surface (SITE-21).
 *
 * The list itself is SITE-20's, one file over
 * (lib/listing/publish-listing-published-price.ts OFF_MARKET_STATUSES): the
 * same four statuses that make the published price a close price rather than
 * an ask are the four that make the page's ask unfulfillable. Re-exported here
 * rather than retyped, because two lists of statuses is exactly the drift that
 * put Coming Soon on the public site — see this file's header.
 *
 * PENDING IS NOT OFF MARKET, and this is the load-bearing half of the rule. A
 * pending home is still marketable: backup offers are real, the tour is real,
 * and the page is a live lead source. Do NOT reach for the inverse of
 * PUBLIC_ACTIVE_STATUSES to answer "is this off market" — that set excludes
 * Pending, so its complement would silently strip the ask off every
 * under-contract home on the site.
 */
export { OFF_MARKET_STATUSES }

/** Public-surface predicate: the home cannot be bought, toured, or asked about. */
export function isPublicOffMarketStatus(s: string | null | undefined): boolean {
  return listingIsOffMarket(s)
}

/**
 * On-market statuses for ADMIN / broker surfaces. Includes Coming Soon because
 * brokers must see their own pre-marketing inventory.
 */
export const ADMIN_ACTIVE_STATUSES: ListingStatus[] = [
  'Active',
  COMING_SOON_STATUS,
  'Active Under Contract',
]

/**
 * Every status a listing holds while it is on the market or under contract:
 * the live inventory a sync must keep current (lib/sync/onMarketReconcile.ts
 * sets our copy of these against the MLS). Not a public list: Coming Soon is in it.
 */
export const LIVE_INVENTORY_STATUSES: ListingStatus[] = [...ADMIN_ACTIVE_STATUSES, ...PUBLIC_PENDING_STATUSES]

/**
 * True when the status is Coming Soon in any casing/spacing the feed sends
 * ("Coming Soon", "ComingSoon", "coming soon").
 */
export function isComingSoonStatus(s: string | null | undefined): boolean {
  return /coming\s*soon/i.test(String(s ?? ''))
}

/**
 * The public gate. False means the listing must not be rendered, linked,
 * counted, or indexed on a public surface.
 *
 * OPEN QUESTION (flagged 2026-07-21, deliberately NOT changed here): a null /
 * empty StandardStatus is currently treated as publicly displayable, because
 * every legacy predicate carried a `StandardStatus.is.null` branch and
 * isActiveStatus() returned true for empty. Tightening that in the same change
 * as the Coming Soon fix risked silently pulling live listings off the site, so
 * this function preserves the existing null behavior. Revisit separately.
 */
export function isPubliclyDisplayableStatus(s: string | null | undefined): boolean {
  return !isComingSoonStatus(s)
}

/**
 * The ONLY `statusFilter` values a public visitor may drive from the URL.
 *
 * Deliberately excludes 'coming_soon' (compliance) and the off-market values
 * 'expired' / 'withdrawn' / 'canceled' / 'off_market' / 'active_or_offmarket'
 * (broker prospecting vocabulary — never a public browse mode). The underlying
 * RPC still understands those; only authenticated admin callers may pass them.
 */
export const PUBLIC_SEARCH_STATUS_FILTERS: string[] = [
  'active',
  'active_and_pending',
  'pending',
  'closed',
  'all',
]

/**
 * PostgREST `.or()` predicate for public on-market reads against the raw
 * `listings` table (mixed-case column). Mirrors PUBLIC_ACTIVE_STATUSES.
 * Excludes the null-status and Coming Soon branches the old string carried.
 */
export const PUBLIC_ACTIVE_OR_PREDICATE =
  'StandardStatus.is.null,StandardStatus.ilike.%Active%,StandardStatus.ilike.%For Sale%'

/**
 * Exact-match twin of PUBLIC_ACTIVE_OR_PREDICATE for queries against the raw
 * `listings` table that also filter by another unindexed column (e.g. "City").
 *
 * Measured 2026-08-02 while fixing /admin/media/banners (60s+ cold load):
 * combining `.eq('"City"', city)` with the ILIKE version above forces Postgres
 * into a per-row pattern-match scan and blows the anon role's 3s
 * statement_timeout for any city with more than a few thousand rows (Redmond
 * failed outright; Bend — 134K rows for that city alone — never would have
 * returned). The exact-match form lets Postgres use plain equality instead:
 * same city, same page, 176ms vs 2795ms+/timeout.
 *
 * Provably an equivalent superset for the isActiveStatus() && isPubliclyDisplayableStatus()
 * combination those callers post-filter with in JS (lib/listing-status.ts +
 * this file): that combined predicate is only ever true for status values
 * that are empty/null, exactly "active" (case-insensitive), or contain
 * "for sale" — the "contains coming soon" branch of isActiveStatus() is
 * always cancelled out by isPubliclyDisplayableStatus()'s negation, so Coming
 * Soon rows are correctly never needed here. Real StandardStatus values in
 * production are a clean RESO enum (Active, Closed, Expired, Canceled,
 * Withdrawn, Pending, Active Under Contract, Coming Soon, For Sale) with no
 * compound/hybrid strings, so "exactly For Sale" and "contains for sale"
 * coincide in practice. Use this ONLY as a coarse pre-filter to shrink what
 * gets fetched — callers must still apply the real isActiveStatus() /
 * isPubliclyDisplayableStatus() check in JS on the results; this predicate is
 * not itself the source of truth.
 */
export const PUBLIC_ACTIVE_OR_PREDICATE_EXACT =
  'StandardStatus.is.null,StandardStatus.eq.Active,StandardStatus.eq.For Sale'

/**
 * Public on-market (active + pending) `.or()` predicate for the raw table.
 */
export const PUBLIC_ON_MARKET_OR_PREDICATE =
  'StandardStatus.is.null,StandardStatus.ilike.%Active%,StandardStatus.ilike.%For Sale%,StandardStatus.ilike.%Pending%,StandardStatus.ilike.%Under Contract%'

/** As above, plus the UnderContract/Contingent spellings the feed also sends. */
export const PUBLIC_ON_MARKET_OR_PREDICATE_WIDE =
  'StandardStatus.is.null,StandardStatus.ilike.%Active%,StandardStatus.ilike.%For Sale%,StandardStatus.ilike.%Pending%,StandardStatus.ilike.%Under Contract%,StandardStatus.ilike.%UnderContract%,StandardStatus.ilike.%Contingent%'

/**
 * PostgREST `.or()` EXCLUSION predicate for lowercase-column MV reads
 * (listing_tile_mv_src and siblings). Mirrors the coming-soon-lockdown view
 * predicate (migration 20260721164833): NULL status passes (not pre-marketing),
 * any Coming Soon spelling is excluded. For service-client reads that bypass
 * the security_barrier view for index pushdown — keep in lockstep with the view.
 */
export const MV_NOT_COMING_SOON_OR_PREDICATE = `standard_status.is.null,standard_status.not.ilike.%${COMING_SOON_STATUS.replace(' ', '%')}%`
